/**
 * PERSONAL SAVED VIEWS ON THE PORTFOLIO.
 *
 * The same idea the Backlog has (src/views.ts, #301), on the roll-up: a person
 * sets the toolbar the way they want it — a preset lens, the dimension filters,
 * a grouping and a sort — names it, and returns to it with one click. Personal
 * to them, following them between machines (stored on the `people` row, migration
 * 0121), never shared in this cut.
 *
 * WHY A PORTFOLIO VIEW IS ITS OWN SHAPE, not the Backlog's `SavedView`. The
 * Backlog filters ISSUES on one board; the Portfolio filters PROJECT ROWS across
 * the whole tenant, by dimensions (owner, health, budget) that have no board to
 * belong to. So a Portfolio view carries the roll-up's own config — the preset
 * `tab`, the dimension `filters`, the `groupBy` and the `sort` — and no board.
 *
 * The four preset lenses are the BUILT-INS: selecting one is a complete config
 * (its tab, nothing filtered, ungrouped, unsorted), so switching lens is a clean
 * reset rather than composing on top of whatever was left on. A view somebody
 * SAVES carries whatever the toolbar held when they pressed +.
 */
import type { GroupBy, TabKey } from './components/portfolio/types';

/** The columns the roll-up can sort by (mirrors RollupTable's Display menu). */
export type PortfolioSortCol =
  | 'work' | 'owner' | 'health' | 'progress' | 'waiting' | 'golive' | 'budget' | 'moved';
export type PortfolioSort = { col: PortfolioSortCol; dir: 'asc' | 'desc' } | null;

/** The dimensions the roll-up can filter by. */
export type PortfolioDim = 'owner' | 'health' | 'budget' | 'waiting' | 'slip';
export type PortfolioFilters = Record<PortfolioDim, string[]>;

export const PORTFOLIO_DIMS: PortfolioDim[] = ['owner', 'health', 'budget', 'waiting', 'slip'];
export const EMPTY_PORTFOLIO_FILTERS: PortfolioFilters = {
  owner: [], health: [], budget: [], waiting: [], slip: [],
};

/** Everything a view captures — the whole toolbar state, and nothing transient
 *  (not which rows are expanded, not the snapshot time). */
export interface PortfolioViewConfig {
  tab: TabKey;
  filters: PortfolioFilters;
  groupBy: GroupBy;
  sort: PortfolioSort;
}

/** A view somebody saved: a config, with an id and a name. */
export interface PortfolioSavedView extends PortfolioViewConfig {
  id: string;
  name: string;
}

/** The four preset lenses, in the order they sit along the top. `all` carries no
 *  count (it hides nothing); the others do. */
export const BUILT_IN_LENSES: { id: TabKey; name: string; counted: boolean }[] = [
  { id: 'all', name: 'All', counted: false },
  { id: 'attention', name: 'Needs attention', counted: true },
  { id: 'waiting', name: 'Waiting on someone', counted: true },
  { id: 'overbudget', name: 'Over budget', counted: true },
];

const BUILT_IN_IDS = new Set<string>(BUILT_IN_LENSES.map((l) => l.id));
export const isBuiltInView = (id: string): id is TabKey => BUILT_IN_IDS.has(id);

/**
 * The lens Portfolio opens on when the person has pinned nothing of their own.
 *
 * "All", so the screen opens showing every project — which is what its own title
 * ("Every project on the tenant") promises, and what someone glancing at the
 * portfolio expects to see first. A person who would rather land on a narrower
 * lens pins one (any lens can be the default), which is the whole point of the
 * feature.
 */
export const FALLBACK_DEFAULT: TabKey = 'all';

/** A built-in lens as a complete config: its tab, nothing else on. */
export function configOfBuiltIn(id: TabKey): PortfolioViewConfig {
  return { tab: id, filters: { ...EMPTY_PORTFOLIO_FILTERS }, groupBy: 'none', sort: null };
}

/** Two filter sets are the same when every dimension holds the same values
 *  (order-independent — the toolbar builds them in selection order). */
function sameFilters(a: PortfolioFilters, b: PortfolioFilters): boolean {
  return PORTFOLIO_DIMS.every((d) => {
    const x = a[d] ?? [];
    const y = b[d] ?? [];
    if (x.length !== y.length) return false;
    const ys = new Set(y);
    return x.every((v) => ys.has(v));
  });
}

/** Whether two configs are identical — the test behind "has this view been
 *  edited since it was loaded". Sort compares by column AND direction; a null
 *  sort (unsorted) only matches another null. */
export function sameConfig(a: PortfolioViewConfig, b: PortfolioViewConfig): boolean {
  if (a.tab !== b.tab) return false;
  if (a.groupBy !== b.groupBy) return false;
  const sa = a.sort;
  const sb = b.sort;
  if ((sa == null) !== (sb == null)) return false;
  if (sa && sb && (sa.col !== sb.col || sa.dir !== sb.dir)) return false;
  return sameFilters(a.filters, b.filters);
}

/** The config an id resolves to — a built-in's fixed config, or a saved view's
 *  stored one. Undefined when a saved id is not (or no longer) present, so the
 *  caller can fall back. */
export function configForId(
  id: string,
  saved: PortfolioSavedView[],
): PortfolioViewConfig | undefined {
  if (isBuiltInView(id)) return configOfBuiltIn(id);
  const v = saved.find((s) => s.id === id);
  return v ? { tab: v.tab, filters: v.filters, groupBy: v.groupBy, sort: v.sort } : undefined;
}

/**
 * Parse one stored view defensively, so a view saved before a field existed (or
 * a half-written row) arrives in the current full shape rather than throwing the
 * moment the toolbar reads `filters.health.length`. Unknown values are dropped
 * to the safe default; the filter map is always complete.
 */
export function normalizeSavedView(raw: unknown): PortfolioSavedView | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'string' ? r.id : '';
  const name = typeof r.name === 'string' ? r.name : '';
  if (!id || !name) return null;
  const tab: TabKey = isBuiltInView(String(r.tab)) ? (r.tab as TabKey) : 'all';
  const groupBy: GroupBy = r.groupBy === 'owner' || r.groupBy === 'health' ? r.groupBy : 'none';
  const rawFilters = (r.filters ?? {}) as Record<string, unknown>;
  const filters = { ...EMPTY_PORTFOLIO_FILTERS };
  for (const d of PORTFOLIO_DIMS) {
    const vals = rawFilters[d];
    filters[d] = Array.isArray(vals) ? vals.filter((v): v is string => typeof v === 'string') : [];
  }
  let sort: PortfolioSort = null;
  const rs = r.sort as Record<string, unknown> | null | undefined;
  if (rs && typeof rs.col === 'string' && (rs.dir === 'asc' || rs.dir === 'desc')) {
    sort = { col: rs.col as PortfolioSortCol, dir: rs.dir };
  }
  return { id, name, tab, filters, groupBy, sort };
}

/** A whole stored list, normalised and stripped of anything unparseable. */
export function normalizeSavedViews(raw: unknown): PortfolioSavedView[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeSavedView).filter((v): v is PortfolioSavedView => v != null);
}
